import type { ReactNode } from 'react';
import { ShieldCheckIcon, CloudIcon } from '@heroicons/react/24/outline';
import type { User } from '../../api';

interface UserTableProps {
  users: User[];
  onEdit?: (user: User) => void;
  onDelete?: (user: User) => void;
  /** Reactivate a deactivated user. Shown as an "Activate" button on inactive rows. */
  onActivate?: (user: User) => void;
  /** Show an Active/Inactive status column (both nodes track is_active). */
  showStatus?: boolean;
  /** Extra per-row actions (e.g. cloud's "Node Access" button). */
  renderExtraActions?: (user: User) => ReactNode;
}

const ROLE_BADGE_STYLES: Record<string, string> = {
  super_admin: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400',
  admin: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400',
  operator: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
  viewer: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400',
};
const DEFAULT_ROLE_BADGE = 'bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400';

export default function UserTable({ users, onEdit, onDelete, onActivate, showStatus = false, renderExtraActions }: UserTableProps) {
  if (!users || users.length === 0) {
    return <p className="py-10 text-center text-[13px] text-muted-foreground">No users found.</p>;
  }

  const showActions = !!(onEdit || onDelete || onActivate || renderExtraActions);
  const isDeactivated = (u: User) => showStatus && u.is_active === false;
  // Cloud-provisioned rows (local node) are managed on the cloud dashboard —
  // read-only here, so edit/delete are hidden for them.
  const isCloudManaged = (u: User) => u.user_type === 'cloud';

  return (
    <div data-tour="users-table" className="overflow-x-auto rounded-xl border border-border bg-card shadow-card">
      <table className="w-full text-left text-[13px]">
        <thead className="border-b border-border bg-muted/50">
          <tr>
            <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Name</th>
            <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Email</th>
            <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Role</th>
            {showStatus && <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Status</th>}
            <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Created</th>
            {showActions && (
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground text-right">Actions</th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {users.map((user) => (
            <tr key={user.id} className={`transition-colors ${isDeactivated(user) ? 'bg-muted/20 opacity-60 hover:opacity-100' : 'hover:bg-muted/30'}`}>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand text-[11px] font-semibold text-white">
                    {user.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
                  </div>
                  <span className="font-medium text-foreground">{user.name || user.email}</span>
                  {isCloudManaged(user) && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-700 dark:bg-sky-900/20 dark:text-sky-400"
                      title="Provisioned from the cloud dashboard — managed there, cannot log in locally"
                    >
                      <CloudIcon className="h-3 w-3" />
                      Cloud
                    </span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 text-muted-foreground">{user.email}</td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize ${ROLE_BADGE_STYLES[user.role || ''] || DEFAULT_ROLE_BADGE}`}>
                  <ShieldCheckIcon className="h-3 w-3" />
                  {user.role?.replace(/_/g, ' ') || '—'}
                </span>
              </td>
              {showStatus && (
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                      user.is_active
                        ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${user.is_active ? 'bg-green-500' : 'bg-muted-foreground'}`} />
                    {user.is_active ? 'active' : 'inactive'}
                  </span>
                </td>
              )}
              <td className="px-4 py-3 text-muted-foreground">
                {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
              </td>
              {showActions && (
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    {renderExtraActions?.(user)}
                    {isCloudManaged(user) ? (
                      <span className="px-2.5 py-1 text-[11px] text-muted-foreground">Managed by cloud</span>
                    ) : (
                      <>
                        {onActivate && user.is_active === false && (
                          <button onClick={() => onActivate(user)} className="rounded-md px-2.5 py-1 text-[11px] font-medium text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 transition-colors">
                            Activate
                          </button>
                        )}
                        {onEdit && (
                          <button onClick={() => onEdit(user)} className="rounded-md px-2.5 py-1 text-[11px] font-medium text-brand hover:bg-brand-light transition-colors">
                            Edit
                          </button>
                        )}
                        {onDelete && (
                          <button onClick={() => onDelete(user)} className="rounded-md px-2.5 py-1 text-[11px] font-medium text-destructive hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                            Delete
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
